import React, { useState } from "react";
import { Formik, Form } from "formik";
import { Typography, Box, Avatar, IconButton, Alert, Button, Grid, LinearProgress } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ReusableDialog from "../../../../../common/dialog/dialog";

const ChildPenPicture = ({ updateFormData, nextStep, prevStep }) => {
  const [open, setOpen] = useState(false); // For dialog
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadedImages, setUploadedImages] = useState([]); // State to hold uploaded images

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter((file) => ["image/jpeg", "image/png"].includes(file.type));

    if (validFiles.length > 0) {
      setSelectedFiles(validFiles);

      // Simulate file upload progress
      validFiles.forEach((file, index) => {
        let progress = 0;
        const interval = setInterval(() => {
          progress += 10;
          setUploadProgress((prevProgress) => ({
            ...prevProgress,
            [file.name]: progress,
          }));
          if (progress === 100) {
            clearInterval(interval);
            // After file upload completes, add the file itself (not URL) to the uploaded images list
            setUploadedImages((prevImages) => [...prevImages, file]);
          }
        }, 200);
      });
    } else {
      alert("Please upload only JPG or PNG images");
    }
  };

  const handleOpenDialog = () => {
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpen(false);
  };

  return (
    <Formik
      initialValues={{}}
      onSubmit={(values) => {
        // Add uploaded images to the form data
        const formData = {
          ...values,
          uploadedImages, // Add the array of image files
        };

        // Pass the form data with images to the backend
        updateFormData({ childPenPictureInfo: formData });
        nextStep(); // Move to next form
      }}
    >
      {({ handleSubmit }) => (
        <Form onSubmit={handleSubmit} className='form-container'>
          {/* Notification Box */}
          <Alert severity='info' sx={{ marginBottom: "20px", backgroundColor: "#F3F4F6" }}>
            <ul>
              <li>
                Staff team trained in Team Teach, safeguarding, 1stAid, Positive Behavior Management, PACE, mandatory
                and additional training undertaken.
              </li>
              <li>Child would be supported through Care Plan, Positive Behavior Support plan and risk assessments.</li>
            </ul>
          </Alert>

          {/* Child's PEN Picture Section */}
          <Typography variant='h6' sx={{ fontWeight: "bold" }} align='left'>
            Child’s PEN Picture
          </Typography>
          <Typography variant='body2' color='textSecondary' gutterBottom>
            Reiterate the child’s pen picture and consider rationale for being placed in care, who and what is important
            to them, education, health, aspirations, interventions, strengths, risks.
          </Typography>

          {/* Avatar Section */}
          <Box mt={2} display='flex' justifyContent='center' alignItems='center'>
            {/* Render multiple Avatars from uploaded images */}
            {uploadedImages.length > 0 ? (
              <Grid container spacing={2} justifyContent='center'>
                {uploadedImages.map((file, index) => (
                  <Grid item xs={12} sm={12} md={3} lg={3} key={index}>
                    <Avatar
                      alt={`Uploaded image ${index + 1}`}
                      src={URL.createObjectURL(file)} // Display image using URL
                      sx={{
                        width: 100,
                        height: 100,
                        borderRadius: "0",
                      }}
                    />
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Avatar
                alt="Child's Picture"
                src='/images/person.png'
                sx={{ width: 150, height: 150, marginRight: "20px", borderRadius: "0" }}
              />
            )}

            <Box mt={2}>
              <IconButton aria-label='edit' onClick={handleOpenDialog}>
                <EditIcon />
              </IconButton>
            </Box>
          </Box>

          {/* Dialog for Adding People */}
          <ReusableDialog
            isOpen={open}
            onClose={handleCloseDialog}
            title='Add another Pen picture?'
            contentText='Add the relevant details to the fields to add another person to the involved parties.'
            onSubmit={() => {
              handleCloseDialog(); // Close dialog after submission
            }}
            submitText='Save'
            cancelText='Cancel'
          >
            <Box mt={2}>
              <input
                style={{ display: "none" }}
                id='file-upload'
                type='file'
                multiple
                accept='.jpg, .jpeg, .png'
                onChange={handleFileChange}
              />
              <label htmlFor='file-upload'>
                <Button component='span' startIcon={<CloudUploadIcon />}>
                  Drag & drop files or browse files
                </Button>
              </label>

              {selectedFiles.length > 0 && (
                <Box mt={2}>
                  <Typography variant='body2' color='textSecondary' gutterBottom>
                    Uploading files
                  </Typography>
                  {selectedFiles.map((file) => (
                    <Box key={file.name} mt={1}>
                      <Typography>{file.name}</Typography>
                      <LinearProgress variant='determinate' value={uploadProgress[file.name] || 0} />
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </ReusableDialog>

          <Grid container justifyContent='space-between' alignItems='center' mt={2}>
            <Grid item>
              <Button type='button' variant='contained' color='primary' onClick={prevStep}>
                Previous
              </Button>
            </Grid>
            <Grid item>
              <Button type='submit' variant='contained' color='primary'>
                Next
              </Button>
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  );
};

export default ChildPenPicture;
